/**
 * Returns the index of the last element in the array where predicate is true, and -1 otherwise.
 * @param predicate find calls predicate once for each element of the array, in descending
 * order, until it finds one where predicate returns true. If such an element is found,
 * findLastIndex immediately returns that element index. Otherwise, findLastIndex returns -1.
 * @param array of elements
 */
export function findLastIndex<T>(
    predicate: (value: T, index: number, array: T[]) => boolean,
    array: T[]
): number {
    for (let i = array.length - 1; i >= 0; --i) {
        if (predicate(array[i], i, array)) return i;
    }

    return -1;
}

/**
 * Merge two arrays by alternating values
 * Example - array1 = [1, 3, 5], array2 = [2, 4, 6], result = [1, 2, 3, 4, 5, 6].
 */
export const mergeArraysByAlternating = <T1, T2>(
    array1: Array<T1> = [],
    array2: Array<T2> = []
): Array<T1 | T2> => {
    return (
        [array1, array2]
            // Get array of arrays where each element is an array of pairs of values of the same index.
            .reduce((acc: Array<Array<T1 | T2>>, arr) => {
                // Make pairs of values.
                arr.forEach((val, i) => (acc[i] = acc[i] || []).push(val));
                return acc;
            }, [])
            .reduce((acc, arr) => acc.concat(arr), [])
    );
};

type ObjectWithKey<Key extends string> = Record<Key, unknown> & Record<string, unknown>;
type ArrayOfObjects<S extends string> = Array<ObjectWithKey<S>>;

export const mergeArrOfObjectsByPropName = <S extends string>(
    source: ArrayOfObjects<S>,
    target: ArrayOfObjects<S>,
    propName: S
): ArrayOfObjects<S> => {
    const targetMap = target.reduce((acc, v) => {
        if (typeof v[propName] === 'undefined') {
            console.warn('Unexpected behavior, merging prop unavailable in data', v, propName);

            return acc;
        }

        acc.set(v[propName], v);

        return acc;
    }, new Map());

    const sourceMap = source.reduce((acc, v) => {
        if (typeof v[propName] === 'undefined') {
            console.warn('Unexpected behavior, merging prop unavailable in data', v, propName);

            return acc;
        }

        acc.set(v[propName], v);

        return acc;
    }, new Map());

    sourceMap.forEach((sourceElement: ObjectWithKey<S>, key: S) => {
        targetMap.has(key)
            ? targetMap.set(key, { ...sourceElement, ...targetMap.get(key) })
            : targetMap.set(key, sourceElement);
    });

    const result: ArrayOfObjects<S> = [];

    targetMap.forEach((value: ObjectWithKey<S>) => result.push(value));

    return result;
};

export const sortBy = <S extends string>(key: S) => {
    return (a: ObjectWithKey<S>, b: ObjectWithKey<S>): 1 | -1 | 0 =>
        a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0;
};

export const sum = ns => ns.reduce((acc, n) => acc + n, 0);

export const toArray = <T>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);
